import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNotificationMessages } from 'hooks/useNotificationMessages';
import { Dialog, DialogContent, DialogTitle, IconButton, Box } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Loading, Table, Typography, colors } from '@leapeasy/ui-kit';
import {
  getRequestUserPropertyAction,
  sendPropertyRequestAction,
} from 'store/actions/delegationActions';
import { failedNotif, successNotif } from 'store/actions/notifActions';
import { getInviteUserColumns } from 'containers/User/InviteNewUser/InvitePage/InviteTableColumns';
import { InvitePropertySelectAllBar } from 'containers/User/InviteNewUser/InvitePage/InvitePropertySelectAllBar';
import { buildRequestAccepter } from './requestAccepter';
import { RequestBuildingDetailDialog } from './RequestBuildingDetailDialog';
import { mapDelegationRequestTableRow } from '../shared/delegationPropertyTable';
import { tableWrapperSx } from 'containers/User/InviteNewUser/InvitePage/InvitePage.styles';
import { GlobalUXState } from 'components/GlobalUXState';
import { useDemoMask } from 'hooks/useDemoMask';

export const RequestPropertyModal = ({ open, title, requestTarget, onClose, onSent }) => {
  const { isDemo } = useDemoMask();
  const dispatch = useDispatch();
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [sortOptions, setSortOptions] = useState({});
  const [awaitingSend, setAwaitingSend] = useState(false);
  const [detailBuilding, setDetailBuilding] = useState(null);
  const { hasSuccess: hasSuccessNotification, hasError: hasErrorNotification } =
    useNotificationMessages();
  const loading = useSelector((state) => state.getIn(['auth', 'loading']));

  const requestUserProperty = useSelector((state) =>
    state.getIn(['delegation', 'requestUserProperty']),
  );
  const requestUserPropertyJSON = requestUserProperty ? requestUserProperty.toJS() : null;
  const buildingRows = requestUserPropertyJSON?.data || [];

  useEffect(() => {
    if (!open || !requestTarget) return;
    setSelectedProperties([]);
    setDetailBuilding(null);
    dispatch(successNotif(null));
    dispatch(failedNotif(null));
    dispatch(getRequestUserPropertyAction(requestTarget));
  }, [open, requestTarget, dispatch]);

  const onToggleProperty = useCallback((propertyId) => {
    setSelectedProperties((existing) =>
      existing.includes(propertyId)
        ? existing.filter((id) => id !== propertyId)
        : [...existing, propertyId],
    );
  }, []);

  const visibleBuildingIds = useMemo(
    () => buildingRows.map((building) => building.building_id),
    [buildingRows],
  );
  const selectedVisibleCount = useMemo(
    () => visibleBuildingIds.filter((id) => selectedProperties.includes(id)).length,
    [visibleBuildingIds, selectedProperties],
  );
  const allSelected =
    visibleBuildingIds.length > 0 && selectedVisibleCount === visibleBuildingIds.length;
  const partiallySelected =
    selectedVisibleCount > 0 && selectedVisibleCount < visibleBuildingIds.length;

  const onToggleAllProperties = useCallback(() => {
    if (allSelected) {
      setSelectedProperties((existing) =>
        existing.filter((id) => !visibleBuildingIds.includes(id)),
      );
      return;
    }
    setSelectedProperties((existing) => [...new Set([...existing, ...visibleBuildingIds])]);
  }, [allSelected, visibleBuildingIds]);

  const buildingById = useMemo(() => {
    const map = new Map();
    buildingRows.forEach((building) => map.set(building.building_id, building));
    return map;
  }, [buildingRows]);

  const tableData = useMemo(
    () => buildingRows.map((building) => mapDelegationRequestTableRow(building, isDemo)),
    [buildingRows, isDemo],
  );

  const columns = useMemo(
    () =>
      getInviteUserColumns({
        selectedProperties,
        onToggleProperty,
        onClickInviteUserDetail: (buildingId) => {
          const building = buildingById.get(buildingId);
          if (building) setDetailBuilding(building);
        },
        showDelegationColumns: true,
        showPrimaryContact: true,
        showDetailColumn: true,
      }),
    [selectedProperties, onToggleProperty, buildingById],
  );

  const handleSubmit = () => {
    const accepter = buildRequestAccepter(requestTarget);
    if (!accepter || selectedProperties.length === 0) return;

    setAwaitingSend(true);
    dispatch(successNotif(null));
    dispatch(failedNotif(null));
    dispatch(
      sendPropertyRequestAction({
        property: selectedProperties,
        accepter,
      }),
    );
  };

  useEffect(() => {
    if (!awaitingSend || !hasSuccessNotification) return;
    setAwaitingSend(false);
    setSelectedProperties([]);
    onSent?.();
  }, [awaitingSend, hasSuccessNotification, onSent]);

  useEffect(() => {
    if (awaitingSend && hasErrorNotification) {
      setAwaitingSend(false);
    }
  }, [awaitingSend, hasErrorNotification]);

  const shouldShowLoading = open && requestTarget && !requestUserPropertyJSON;

  return (
    <>
      <RequestBuildingDetailDialog
        building={detailBuilding}
        onClose={() => setDetailBuilding(null)}
      />
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pr: 1,
          }}
        >
          <Typography variant="h3" style={{ color: colors.purple[900], fontWeight: 500 }}>
            {title || 'Request properties'}
          </Typography>
          <IconButton aria-label="close" onClick={onClose}>
            ×
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" style={{ color: colors.neutral[700], marginBottom: 16 }}>
            Select one or more properties, then submit your delegation request. Use the detail icon
            to view property information before requesting.
          </Typography>

          <Box sx={tableWrapperSx}>
            {shouldShowLoading ? (
              <Loading size="large" label="Loading properties..." />
            ) : (
              <>
                {buildingRows.length > 0 && (
                  <InvitePropertySelectAllBar
                    allSelected={allSelected}
                    partiallySelected={partiallySelected}
                    onToggle={onToggleAllProperties}
                    visibleCount={buildingRows.length}
                  />
                )}
                <Table
                  showEmptyState
                  emptyContent={
                    <GlobalUXState
                      variant="noData"
                      compact
                      description="No properties are available to request for this selection."
                    />
                  }
                  data={tableData}
                  columns={columns}
                  sortOptions={sortOptions}
                  onChangeSort={setSortOptions}
                />
              </>
            )}
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
              mt: 3,
              pb: 1,
            }}
          >
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={selectedProperties.length === 0 || (loading && awaitingSend)}
            >
              {loading && awaitingSend ? 'Sending...' : 'Submit request'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};
